/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect, useState, useRef } from 'react';
import { PropTypes } from 'prop-types';
import styled from 'styled-components';
import ReactTooltip from 'react-tooltip';
import { COLORS, DEVICES_SIZES } from 'apps/web_forms/utils/constants';
import Arrow from 'apps/web_forms/assets/arrow.svg';
import { noop } from 'lodash';
import useWindowSize from 'apps/web_forms/hooks/useWindowSize';
import formIcon from 'apps/web_forms/assets/form.png';

const listElementClassName = 'form-list-element';

const StyledListElement = styled.div`
  width: 100%;
  margin: 0;
  position: relative;

  .list-element-container {
    width: 100%;
    font-size: inherit;
    padding: 0.5em 1em 0.5em 0;
    cursor: pointer;
    border: 1px solid #d8d8d8;
    background-color: white;
    border-radius: 3px;
    color: ${COLORS.textColor};
    display: flex;
    align-items: flex-start;
    box-sizing: border-box;
    position: relative;

    .disabled {
      color: #a5a5a5;
    }
  }

  .form-icon-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 2.5em;

    div {
      width: 3em;
      text-align: center;
      margin: auto;
    }

    img {
      opacity: 0.7;
    }

    svg {
      width: 3em;
    }

    span {
      font-size: 0.75em;
    }
  }

  .form-info {
    height: 2.5em;
    width: 75%;
    padding-top: 0.3em;
    box-sizing: border-box;
    padding-left: 0.6em;
    border-left: 1px solid ${COLORS.borderGrey};
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
    text-align: left;
    transition: 250ms;

    h3 {
      margin: 0;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      font-size: 1em;
      font-weight: 900;
      padding-bottom: 0.1em;
    }

    h4 {
      margin: 0;
      position: relative;
      top: 0.2em;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      font-size: 0.8em;
    }

    p {
      margin-top: 0.25em;
      font-size: 0.8em;
    }
  }

  .more-info-container {
    overflow: hidden;
    display: ${(props) => (!props.isOpen ? 'none' : 'unset')};

    .more-info {
      opacity: ${(props) => (props.isOpen ? '1' : '0')};
      position: absolute;
      transition: opacity 250ms;

      button {
        border: none;
        background: ${COLORS.lighterGrey};
        color: ${COLORS.darkerPrimary};
        font-size: 0.75em;
        padding: 0.75em 3em;
        margin: 1em;
        border-radius: 5px;
        font-weight: bold;

        &:hover {
          opacity: 0.8;
        }
      }

      button.red {
        color: ${COLORS.red};
      }

      button.highlighted-button {
        background: ${COLORS.darkerPrimary};
        color: ${COLORS.lighterGrey};
      }

      button.disabled-button {
        background: #a5a5a5;
        color: #4d4c4d;

        &:hover {
          opacity: 1;
        }
      }

      .extra-info {
        font-size: 0.75em;
        color: ${COLORS.textColor};
      }
    }
  }

  .more-button {
    outline: none;
    position: absolute;
    right: 0;
    top: 0;
    margin-top: 0.6em;
    height: 2.5em;
    width: 6em;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: inherit;
    background: transparent;
    border: none;

    span {
      font-size: 0.8em;
      font-weight: bold;
      margin-right: 0.5em;
      color: ${COLORS.textColor};
      opacity: ${(props) => (props.isOpen ? 0 : 1)};
      transition: 250ms;

      @media (max-width: ${DEVICES_SIZES.tablet}) {
        display: none;
      }
    }

    svg {
      height: 0.9em;
      transform: ${(props) => (props.isOpen ? 'rotate(-90deg)' : 'none')};
      transition: 250ms;
    }

    @media (max-width: ${DEVICES_SIZES.tablet}) {
      width: auto;
      padding: 15px;
    }
  }

  @media (min-width: ${DEVICES_SIZES.desktop}) {
    width: 49%;
  }

  .toolTip {
    font-size: 0.8em;
    pointer-events: auto;
    &:hover {
      visibility: visible;
      opacity: 1;
    }
  }
`;

const FormListElement = ({
  // iconComponent: IconComponent,
  // iconHeight,
  // iconColor,
  iconLabel,
  identifier,
  title = '',
  subtitle,
  label = '',
  description = '',
  geoInfo,
  onClick,
  buttons,
  moreOption = false,
  onMoreInfoClickHandler,
  openByDefault,
  disabled,
  index,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [moreInfoMessage, setMoreInfoMessage] = useState(false);
  const [itemWidth, setItemWidth] = useState(0);
  const formInfoRef = useRef(null);
  const moreInfoRef = useRef(null);
  const hasButtons = buttons && Array.isArray(buttons) && buttons.length > 0;
  const windowSize = useWindowSize();

  useEffect(() => {
    if (moreInfoRef && formInfoRef) {
      if (isOpen) {
        formInfoRef.current.style.height = `calc(2.5em + ${
          moreInfoRef.current?.offsetHeight || 0
        }px)`;
      } else {
        formInfoRef.current.style.height = `2.5em`;
      }
    }
  }, [isOpen, moreInfoRef]);

  useEffect(() => {
    setIsOpen(openByDefault);
  }, [openByDefault]);

  useEffect(() => {
    if (geoInfo) {
      let message;

      if (geoInfo.distance.result === null && !geoInfo.distance.error) {
        message = (
          <p>
            Calculating your distance to <b>{geoInfo.location}</b>
          </p>
        );
      }

      if (geoInfo.distance.result !== null) {
        message = (
          <p>
            You are <b> {geoInfo.distance.result} </b> away from{' '}
            <b>{geoInfo.location}</b>
          </p>
        );
      }

      if (geoInfo.distance.outOfRange) {
        message = (
          <p>
            <b style={{ color: COLORS.red }}>Out of Range:</b>{' '}
            <b> {geoInfo.distance.result} </b> away from{' '}
            <b>{geoInfo.location}</b>
          </p>
        );
      }

      if (geoInfo.distance.error) {
        message = (
          <p>
            Failed to get your location. Enable location services and refresh
            the page.&nbsp;
            <b>{geoInfo.location}</b>
          </p>
        );
      }
      setMoreInfoMessage(message);
    }
  }, [geoInfo]);

  useEffect(() => {
    const listElements = document.querySelectorAll(`.${listElementClassName}`);
    if (listElements) {
      const firstListElement = listElements[0];
      const width = firstListElement.offsetWidth;
      if (width && typeof width === 'number' && width !== itemWidth) {
        setItemWidth(firstListElement.offsetWidth);
      }
    }
  }, [windowSize]);

  const thereIsMoreInfo = hasButtons || description;

  // Give element a keyboard handler that lets user "click" by pressing space or enter, like buttons do by default, for accessibility
  const getButtonLikeKeyHandler = (clickHandler) => {
    const keyHandler = (event) => {
      if (event.keyCode === 13 || event.keyCode === 32) {
        // enter or space
        clickHandler();
      }
    };
    return keyHandler;
  };

  const onFormItemClick = isOpen || disabled ? noop : () => onClick(identifier);

  const toolTipOffest = {
    left: itemWidth / 2 - 100,
  };

  return (
    <StyledListElement
      key={identifier}
      isOpen={isOpen}
      className={listElementClassName}
      data-tip={!label ? title : `${title} - ${label}`}
      data-delay-show='1000'
      data-for={identifier}
    >
      <ReactTooltip
        className='toolTip'
        id={identifier}
        effect={'solid'}
        offset={toolTipOffest}
      />
      <div
        data-testid='form-item'
        className='list-element-container'
        onClick={onFormItemClick}
        role='button'
        onKeyDown={getButtonLikeKeyHandler(onFormItemClick)}
        tabIndex={0}
      >
        <div className='form-icon-container'>
          <div>
            <img src={formIcon} height="23px" />
            {iconLabel && (
              <span className={disabled ? 'disabled' : null}>{iconLabel}</span>
            )}
          </div>
        </div>
        <div className='divider' />
        <div className='form-info' ref={formInfoRef}>
          <h3 className={disabled ? 'disabled' : null}>
            {
              !label ? (
                title
              ) : (
                <>
                  {`${title} - `}
                  <span style={{ color: COLORS.red }}>{label}</span>
                </>
              ) /** make color variable? */
            }
          </h3>
          <h4 className={disabled ? 'disabled' : null}>{subtitle}</h4>

          <div className='more-info-container'>
            <div className='more-info' ref={moreInfoRef}>
              {description && <p>{description}</p>}

              <div>
                {hasButtons &&
                  buttons.map(
                    (
                      button // TODO: use RenderButtons component w/ standard buttons instead?
                    ) => (
                      <button
                        key={button.id}
                        type='button'
                        className={`
                    ${button.highlighted ? 'highlighted-button' : ''}
                    ${button.red ? 'red' : ''}
                    ${button.disabled ? 'disabled-button' : ''}
                    `}
                        onClick={() => button.onClick(identifier)}
                        disabled={button.disabled}
                      >
                        {button.label}
                      </button>
                    )
                  )}
              </div>

              {moreInfoMessage && moreInfoMessage}
            </div>
          </div>
        </div>
      </div>
      {moreOption && thereIsMoreInfo && (
        <button
          aria-label='More'
          type='button'
          className='more-button'
          onClick={() => {
            setIsOpen((currentValue) => !currentValue);
            onMoreInfoClickHandler(index);
          }}
        >
          <span>More</span>
          <Arrow />
        </button>
      )}
    </StyledListElement>
  );
};

export const formListElementPropTypes = {
  // iconComponent: PropTypes.elementType.isRequired,
  // iconHeight: PropTypes.string,
  // iconColor: PropTypes.string,
  iconLabel: PropTypes.string,
  identifier: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  description: PropTypes.string.isRequired,
  geoInfo: PropTypes.shape({
    distance: PropTypes.shape({
      result: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      error: PropTypes.bool,
      outOfRange: PropTypes.bool,
    }),
    location: PropTypes.string,
  }),
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      highlighted: PropTypes.bool,
      disabled: PropTypes.bool,
      red: PropTypes.bool,
    })
  ),
  onClick: PropTypes.func,
  moreOption: PropTypes.bool,
  openByDefault: PropTypes.bool,
  disabled: PropTypes.bool,
  onMoreInfoClickHandler: PropTypes.func,
  index: PropTypes.number.isRequired,
  label: PropTypes.string,
};

export const formListElementDefaultProps = {
  iconHeight: '1.25em',
  iconLabel: '',
  iconColor: 'red',
  geoInfo: null,
  buttons: [],
  onClick: () => {},
  moreOption: false,
  openByDefault: false,
  disabled: false,
  onMoreInfoClickHandler: () => {},
  label: '',
  subtitle: undefined,
};

FormListElement.propTypes = formListElementPropTypes;

FormListElement.defaultProps = formListElementDefaultProps;

export default FormListElement;
